import { Route as Woute, Switch, Redirect, useParams } from 'wouter'
import { useAppStore } from '@/state'

import { amplitudeInit } from './lib/amplitude'
import { ProjectsPage } from '@/pages/ProjectsPage/page'
import { CADPage } from '@/pages/CADPage'
import { DocumentPage } from '@/pages/DocumentPage'
import { LoginPage } from '@/pages/LoginPage'
import { SignUpPage } from '@/pages/SignUpPage'
import { TechnicianPage } from '@/pages/TechnicianPage'
import { useVerifyLogin } from '@/services/queries/authentication'
import { TermsOfServiceDialog } from '@/components/core/TermsOfServiceDialog'
import { NavHomeButton } from './components/nav/NavHomeButton'
import { Spinner } from './components/ui/spinner'
import CanvasProvider from './lib/cad/CanvasProvider'
import { GarbageCollector } from './components/core/GarbageCollector'
import { PDFPage } from './pages/PDFPage'
import { setUser } from '@sentry/react'
import { useEffect } from 'react'

type AuthParams = {
  redirectUrl?: string
  disableHomeButton?: boolean
  disableTermsOfAgreement?: boolean
  jwtAuth?: boolean
}

export const auth = (
  Component: React.ComponentType<any>,
  params?: AuthParams,
) => {
  const Authed = (props: any) => {
    const { isVerifyingLoginStatus, isLoggedIn, user } = useVerifyLogin({
      jwtAuth: params?.jwtAuth,
    })
    const initViewWorker = useAppStore((state) => state.initViewWorker)
    const initServiceWorker = useAppStore((state) => state.initServiceWorker)

    useEffect(() => {
      if (user && isLoggedIn) {
        amplitudeInit(user.email)
        setUser({
          id: user.id as number,
          username: `${user.first_name} ${user.last_name}`,
          email: user.email,
          ip_address: '{{auto}}',
          is_active: user.is_active,
        })
      }
    }, [isLoggedIn, user])

    if (isVerifyingLoginStatus) {
      return (
        <div className="w-full h-screen flex flex-col">
          <div className="bg-primary-40 h-16 flex items-center pl-8">
            <NavHomeButton disabled={Boolean(params?.disableHomeButton)} />
          </div>
          <div className="flex flex-col items-center justify-center mt-20">
            <Spinner />
          </div>
        </div>
      )
    }

    if (isLoggedIn) {
      initViewWorker()
      initServiceWorker()
      return (
        <>
          <GarbageCollector />
          {!params?.disableTermsOfAgreement && <TermsOfServiceDialog />}
          <Component {...props} />
        </>
      )
    }

    return (
      <Redirect
        to={
          params?.redirectUrl ||
          `~/login?continue_to=${encodeURIComponent(document.location.pathname + document.location.search)}`
        }
        replace
      />
    )
  }

  Authed.displayName = Component.displayName || Component.name

  return Authed
}

interface RouteProps {
  path: string
  base?: boolean
  children?: React.ReactNode
  component?: React.ComponentType<any>
  nest?: boolean
  isProtected?: boolean
}

const Route = ({
  path,
  children,
  component,
  nest = false,
  isProtected = true,
}: RouteProps) => {
  return (
    <Woute
      path={path}
      component={component && (isProtected ? auth(component) : component)}
      nest={nest}
    >
      {children}
    </Woute>
  )
}

const AuthedCanvasProvider = auth(
  ({ children }: { children: React.ReactNode }) => (
    <CanvasProvider>{children}</CanvasProvider>
  ),
)

const OnShapeRedirect = () => {
  useEffect(() => {
    window.location.href = `/onshape.html${document.location.search}`
  }, [])
  return (
    <div className="p-6">
      <Spinner />
    </div>
  )
}

export const Router = () => {
  return (
    <Switch>
      <Route path="/" component={ProjectsPage} />
      <Route path="/p/:projectId" component={ProjectsPage} />
      <Route path="/p/:projectId/cad" component={RedirectToProjectPage} />
      <Route path="/p/:projectId/document/:documentId" nest>
        <AuthedCanvasProvider>
          <Route path="/cad" component={CADPage} />
          <Route path="/edit" component={DocumentPage} />
        </AuthedCanvasProvider>
        <Route path="/print" component={PDFPage} />
        <Route path="/tech" component={TechnicianPage} />
      </Route>
      <Route path="/login" component={LoginPage} isProtected={false} />
      <Route path="/signup" component={SignUpPage} isProtected={false} />
      <Route
        path="/onshape.html/*"
        component={OnShapeRedirect}
        isProtected={false}
      />
      <Route path="/*.html" component={ProjectsPage} isProtected={false} />
      <Woute>404: No such page!</Woute>
    </Switch>
  )
}

const RedirectToProjectPage = () => {
  const { projectId } = useParams<{ projectId: string }>()

  return <Redirect to={`~/p/${projectId}`} />
}
